import { Flex, Typography } from "antd"
import cls from "classnames"

import Private from "../../../../../../Components/Private"
import Icon from "../../../../../../Icon"
import { formatCurrency } from "../../../../../../Utils/utils"
import { DEFAULT_EPS_ORDERING, withEpsSort } from "../../../../components/Monitoring/epsSorting"
import HighlightedText from "../../../../components/Monitoring/HighlightedText/HighlightedText"
import MonthRowName from "../../../../components/Monitoring/MonthRowName/MonthRowName"
import { formatRiskTooltip, getRiskLevel } from "../../../../components/Monitoring/riskLevel"
import RiskPill from "../../../../components/Monitoring/RiskPill/RiskPill"
import { formatCount } from "../../../../components/Table/columns"
import { formatRate } from "../../../utils/formatNumber"

const { Text } = Typography

export const getColumns = ({
  t,
  expandedKeys,
  isMobile,
  selectedMetric,
  vampLimit,
  isIncognito,
  sortable = false,
  search = ""
}) => {
  const isMonth = (record) => record?.row_type === "month"

  const allColumns = [
    {
      key: "billing_descriptor",
      title: t("Billing descriptor"),
      dataIndex: "billing_descriptor",
      render: (_, record) => {
        const row_type = record?.row_type
        const isExpandable = record?.months?.length > 0 && row_type !== "month"
        const isExpanded = expandedKeys.includes(record?.unique_id)

        // The month rows stay readable in Incognito mode — only the descriptor name itself is
        // hidden (CHA1-1357).
        if (row_type === "month") return <MonthRowName month={record} />
        return (
          <Flex gap={8} align="center">
            {isExpandable && (
              <Icon
                type="chevron_down_small"
                className={cls("chevron_down", { expanded: isExpanded })}
              />
            )}
            <Text strong>
              {_ == null ? (
                "—"
              ) : (
                <Private>
                  <HighlightedText text={_} query={search} />
                </Private>
              )}
            </Text>
          </Flex>
        )
      }
    },
    {
      key: "vamp_rate",
      title: t("VAMP ratio"),
      dataIndex: "vamp_rate",
      align: "right",
      render: (_, record) => {
        if (!getIsValidNumber(_)) return <Text strong={!isMonth(record)}>—</Text>

        // Hidden in Incognito mode; the fraud / dispute / transaction counts below stay visible.
        const value = <Private>{formatRate(_)}</Private>
        if (isMonth(record)) return <Text>{value}</Text>

        // The tooltip repeats the ratio, so it is dropped while Incognito masks the value.
        const tooltip =
          vampLimit && !isIncognito
            ? formatRiskTooltip({
                rateName: "VAMP ratio",
                rate: _,
                limitName: "limit",
                limit: vampLimit
              })
            : null

        return (
          <Text strong>
            <RiskPill level={getRiskLevel(_, vampLimit)} tooltip={tooltip}>
              {value}
            </RiskPill>
          </Text>
        )
      }
    },
    {
      key: "fraud_count",
      title: "Fraud count",
      dataIndex: "fraud_count",
      align: "right",
      render: (_, record) => {
        return <Text strong={!isMonth(record)}>{getIsValidNumber(_) ? formatCount(_) : "—"}</Text>
      }
    },
    {
      key: "dispute_count",
      title: "Dispute count",
      dataIndex: "dispute_count",
      align: "right",
      render: (_, record) => {
        return <Text strong={!isMonth(record)}>{getIsValidNumber(_) ? formatCount(_) : "—"}</Text>
      }
    },
    {
      key: "transaction_count",
      title: "Transaction count",
      dataIndex: "transaction_count",
      align: "right",
      render: (_, record) => {
        return <Text strong={!isMonth(record)}>{getIsValidNumber(_) ? formatCount(_) : "—"}</Text>
      }
    },
    {
      key: "transaction_volume",
      title: "Transaction volume",
      dataIndex: "transaction_volume",
      align: "right",
      render: (_, record) => {
        return (
          <Text strong={!isMonth(record)}>
            {getIsValidNumber(_) ? <Private>{formatCurrency(_, true)}</Private> : "—"}
          </Text>
        )
      }
    }
  ]

  const columns = allColumns.map((column) =>
    withEpsSort(column, {
      sortable,
      isText: column.key === "billing_descriptor",
      defaultOrdering: DEFAULT_EPS_ORDERING
    })
  )

  if (isMobile) {
    return columns.filter((col) => col.key === "billing_descriptor" || col.key === selectedMetric)
  }

  return columns
}

const getIsValidNumber = (value) => typeof value === "number" && !isNaN(value)
